import { type ElementType, useEffect, useRef } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, type SxProps } from '@mui/material';
import type { PlanningEntityIdentifier, PlanningEntityType } from 'generated';

import { PRIMARY, WHITE } from 'constants/colors';
import { brightness } from 'utils/misc';

interface DroppableCellPragmaticProps {
  id: string;
  component?: ElementType<HTMLElement>;
  label?: string;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
  scheduleStart: string;
  sx?: SxProps;
}

function DroppableCellPragmatic({
  id,
  component,
  label,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier,
  scheduleStart,
  sx
}: DroppableCellPragmaticProps) {
  'use memo';
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const cleanup = dropTargetForElements({
      element: el,
      getData: () => ({
        id,
        planningEntityType,
        planningEntityId,
        planningEntityIdentifier,
        scheduleStart,
        isDroppableCell: true
      }),
      onDragEnter: () => el.setAttribute('data-drag-over', ''),
      onDragLeave: () => el.removeAttribute('data-drag-over'),
      onDrop: () => el.removeAttribute('data-drag-over')
    });

    return () => {
      cleanup();
      el.removeAttribute('data-drag-over');
    };
  }, [
    id,
    planningEntityType,
    planningEntityId,
    planningEntityIdentifier,
    scheduleStart
  ]);

  const idleBg = 'rgba(255, 255, 255, 0.75)';

  return (
    <Box
      component={component || 'div'}
      ref={ref}
      className="cell-droppable-container"
      height="100%"
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        className="cell-droppable"
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          height: '100%',
          minHeight: '30px',
          padding: '10px 0',
          color: '#6b6b6b',
          fontSize: '10px',
          fontFamily: 'SourceSansPro-Regular',
          backgroundColor: WHITE,
          borderRadius: '4px',
          border: `2px solid ${idleBg}`,
          transition: 'border-color 0.2s ease-in-out',
          '.cell-droppable-container[data-drag-over] &': {
            color: PRIMARY,
            backgroundColor: brightness(PRIMARY, 90),
            borderColor: PRIMARY
          },
          ...(sx && sx)
        }}
      >
        {label ?? ''}
      </Box>
    </Box>
  );
}

export default DroppableCellPragmatic;
